import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Image } from 'react-native';
import { FileUpload } from '@shared/components/blocks/types';
import { Signature } from '@shared/data/entity/fields/SignatureField';
import { useSelector } from '@shared/store';
import { API } from '@shared/types/api';
import { containedImageUrl, fullSizeImageUri, greyscaleImageUri } from '@shared/util/media/image';
import { useMountedRef } from './useMountedRef';

interface Props {
  image: FileUpload | API.ChatAttachmentFragment | Signature;
  containerWidth?: number;
  containerHeight?: number;
  size?: 'full' | 'contain';
  grayscale?: boolean;
  heightOffset?: number;
  widthOffset?: number;
}

interface ImageSrcAndDimensions {
  src: string;
  width: number;
  height: number;
  ratio: number;
  isLandscape: boolean;
  isPortrait: boolean;
}

/**
 * Given an image object and its container width/height,
 * returns an Imgix src and the width and height of the image.
 *
 * Will return the fullsize image src or an src for the max image size
 * that will fit in the given container dimensions.
 *
 * Returns ratio, isLandscape, and isPortrait convenience values.
 */
export function useImageSrcAndDimensions({
  image,
  containerWidth,
  containerHeight,
  size = 'full',
  grayscale = false,
  widthOffset = 0,
  heightOffset = 0,
}: Props): ImageSrcAndDimensions {
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const viewportHeight = useSelector(state => state.appState.viewportHeight);
  const mounted = useMountedRef();
  const [intrinsicDimensions, setIntrinsicDimensions] = useState([image.width, image.height]);
  const ratio = (intrinsicDimensions[0] ?? 1) / (intrinsicDimensions[1] ?? 1);
  const isLandscape = ratio >= 1;
  const isPortrait = ratio < 1;

  const colorUrl = grayscale ? greyscaleImageUri(image.url) : image.url;
  const src =
    size === 'full'
      ? fullSizeImageUri(colorUrl)
      : containedImageUrl(colorUrl, [containerWidth ?? 0, containerHeight ?? 0]);

  // Fall back to Image.getSize only when the attachment doesn't carry its own
  // width/height (e.g. images from older clients or certain attachment types).
  useEffect(() => {
    if (!isNil(intrinsicDimensions[0]) && !isNil(intrinsicDimensions[1])) {
      return;
    }

    Image.getSize(src, (w, h) => {
      if (mounted.current) {
        setIntrinsicDimensions([w, h]);
      }
    });
  }, [intrinsicDimensions, src, mounted]);

  // Compute display dimensions synchronously so the container never starts at
  // the 56×56 minimum before jumping to its real size. This prevents the layout
  // shift that causes the scroll view to over-adjust when images first appear.
  const dimensions = useMemo(() => {
    const [w, h] = intrinsicDimensions;
    if (isNil(w) || isNil(h)) {
      return [undefined, undefined];
    }
    const maxWidth = !isNil(containerWidth) ? containerWidth : viewportWidth;
    const maxHeight = !isNil(containerHeight) ? containerHeight : viewportHeight;
    if (isNil(maxWidth) || isNil(maxHeight)) {
      return [undefined, undefined];
    }
    // "contain" scaling: use the most restrictive scale so image fits both dimensions
    const scale = Math.min(maxWidth / w, maxHeight / h, 1);
    return [w * scale + widthOffset, h * scale + heightOffset];
  }, [
    intrinsicDimensions,
    containerWidth,
    containerHeight,
    widthOffset,
    heightOffset,
    viewportWidth,
    viewportHeight,
  ]);

  return {
    src,
    width: dimensions[0] ?? 0,
    height: dimensions[1] ?? 0,
    ratio,
    isLandscape,
    isPortrait,
  };
}
